import type { TypesVersionInOut } from '@festina/configuration-general-types/configuration'
import { assertNotEmpty, typeUtils } from '@festina-life/core'
import {
  FormikFileInput,
  FormikTextArea,
  FormikTextInput,
} from '@festina-life/ui/formikForms'
import { useFormikContext, useTranslate } from '@festina-life/ui/hooks'
import { useEffect } from 'react'

import {
  type BranchTypesInput,
  isTypesVersionInOut,
  type LayerTypesVersionsInOut,
} from '../../../uiModels/branchTypes/types'
import { loadTypedDataFromFile } from '../../branchCreator/helpers'

const validate = typeUtils.createValidator<BranchTypesInput>()

type Props = {
  isRequired?: boolean
}

export const BranchTypeFormInputs = ({ isRequired }: Props) => {
  const translate = useTranslate()
  const { values, setFieldValue, setFieldTouched } =
    useFormikContext<BranchTypesInput>()
  const currentFilesValue = values.typeVersionFile

  useEffect(() => {
    const updateNameAndDescriptionFields = async () => {
      if (currentFilesValue?.length) {
        const file = currentFilesValue.at(0)
        assertNotEmpty(file)

        const versionInOut = await loadTypedDataFromFile<
          TypesVersionInOut | LayerTypesVersionsInOut
        >(file, ['TypesVersionInOut', 'LayerTypesVersionsInOut'])

        const typesVersionName = isTypesVersionInOut(versionInOut)
          ? versionInOut.name
          : versionInOut.layerTypesVersions.at(0)?.typesVersion.name

        const typesVersionDescription = isTypesVersionInOut(versionInOut)
          ? versionInOut.description
          : versionInOut.layerTypesVersions.at(0)?.typesVersion.description
        await setFieldValue(
          validate('typesVersionName'),
          typesVersionName,
          true,
        )

        await setFieldValue(
          validate('typesVersionDescription'),
          typesVersionDescription,
          true,
        )
      } else {
        await setFieldValue(validate('typesVersionName'), '')
        await setFieldValue(validate('typesVersionDescription'), '')
      }
    }

    void updateNameAndDescriptionFields()
  }, [currentFilesValue, setFieldTouched, setFieldValue])

  // Fields need to be marked as touched in a side effect in order for the validation to be done with the most recent value
  useEffect(() => {
    if (values.typesVersionName) {
      void setFieldTouched(validate('typesVersionName'), true)
    }
    if (values.typesVersionDescription) {
      void setFieldTouched(validate('typesVersionDescription'), true)
    }
  }, [setFieldTouched, values.typesVersionDescription, values.typesVersionName])

  return (
    <>
      <FormikFileInput
        isRequired={isRequired}
        label={translate('branchCreationForm.typesFile.label')}
        name={validate('typeVersionFile')}
        accept=".json"
        multiple={false}
      />
      <FormikTextInput
        isRequired={isRequired}
        name={validate('typesVersionName')}
        label={translate('branchCreationForm.typesVersionName.label')}
        disabled={!values.typeVersionFile?.length}
      />
      <FormikTextArea
        isRequired={isRequired}
        name={validate('typesVersionDescription')}
        label={translate('branchCreationForm.typesVersionDescription.label')}
        disabled={!values.typeVersionFile?.length}
      />
    </>
  )
}
